import {
    ApplicationHelpType,
    NoteTypes,
    ReviewStatusTypesEnum,
} from '../../../utilities/Constants';

import { CaseName } from '../../case/CaseName';
import { Help } from '../../shared/Help';
import { ICaseTitleModel } from '../../../interfaces/ICase';
import { IMultiSelectOption } from '../../../interfaces/ILookup';
import { INoteModel } from '../../../interfaces/INote';
import { IconButton } from '../../../shared/buttons/IconButton';
import { Link } from 'react-router-dom';
import { Stack } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

interface INoteEditorHeader {
    caseTitle?: ICaseTitleModel;
    currentNote: INoteModel;
    hasUnsavedChanges?: boolean;
    isAllowLocalEditSystemSet: boolean;
    isCaseNote: boolean;
    isStarred: boolean;
    isPrivate: boolean;
    isPrivateCheckboxDisabled: boolean;
    noteOriginEntityURL?: string;
    onAddRelatedButtonClick: () => void;
    onBackButtonClick: () => void;
    onCancelButtonClick: () => void;
    onEditButtonClick: () => void;
    onHistoryButtonClick: () => void;
    onIsStarredCheckChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onLocalEditCheckChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onPrivateCheckChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onIsReviewRequiredChanged: () => void;
    onSaveButtonClick: () => void;
    onWatchListButtonClick: () => void;
    strategyName: string;
    readonly?: boolean;
    selectedEntityItems: IMultiSelectOption[];
    showAddRelatedButton?: boolean;
    showEditButton?: boolean;
    showHistoryButton?: boolean;
    showAdditionalNoteFields?: boolean;
    showWatchListButton?: boolean;
    noteAssociationTitle?: string;
    noteAssociationIcon?: string;
}

const ReviewStatusLabels: Record<ReviewStatusTypesEnum, string> = {
    [ReviewStatusTypesEnum.ReviewRequired]: 'Review Required',
    [ReviewStatusTypesEnum.Reviewed]: 'Reviewed',
};

export default function NoteEditorHeader(props: INoteEditorHeader) {
    const getNoteTypeDisplayName = () => {
        switch (props.currentNote.type?.id) {
            case NoteTypes.StrategyNote:
                return 'Strategy Note';
            case NoteTypes.PackageSettlementNote:
                return 'Deal Note';
            case NoteTypes.ZoneNote:
                return 'Zone Note';
            default:
                return 'Note';
        }
    };

    const nonCaseNoteTitle =
        props.currentNote.originEntityName ||
        props.strategyName ||
        props.currentNote.packageDealName;

    const statusId = props.currentNote?.reviewStatus ? props.currentNote.reviewStatus?.id : 0;
    const isReviewed = statusId == ReviewStatusTypesEnum.Reviewed;
    const reviewStatusText =
        statusId in ReviewStatusLabels
            ? ReviewStatusLabels[statusId as ReviewStatusTypesEnum]
            : ' ';

    return (
        <Stack
            direction="horizontal"
            style={{
                justifyContent: 'space-between',
                marginBottom: 10,
            }}
        >
            <h1 style={{ marginBottom: 0 }}>
                {props.isCaseNote ? (
                    <CaseNoteHeaderTitle caseTitle={props.caseTitle} />
                ) : (
                    <>
                        {props.noteOriginEntityURL ? (
                            <Link target="_blank" to={props.noteOriginEntityURL}>
                                {nonCaseNoteTitle}
                            </Link>
                        ) : (
                            <>{nonCaseNoteTitle}</>
                        )}
                        {nonCaseNoteTitle ? ' - ' : ''}
                        {getNoteTypeDisplayName()}
                    </>
                )}
            </h1>
            <Stack direction="horizontal" gap={2} className="print-hide-display">
                {props.showAddRelatedButton && (
                    <AddRelatedButton
                        hasItems={(props.selectedEntityItems ?? []).length > 0}
                        onClick={props.onAddRelatedButtonClick}
                        icon={props.noteAssociationIcon}
                        title={props.noteAssociationTitle}
                    />
                )}
                {props.showAdditionalNoteFields && (
                    <>
                        {[
                            ReviewStatusTypesEnum.ReviewRequired,
                            ReviewStatusTypesEnum.Reviewed,
                        ].includes(statusId) && (
                            <IconButton
                                buttonCssClass="btn btn-no-bg text-gray"
                                icon={isReviewed ? 'fas fa-clipboard-check' : 'far fa-clipboard'}
                                disabled={props.readonly}
                                text={reviewStatusText}
                                onClick={props.onIsReviewRequiredChanged}
                            />
                        )}
                        <div style={{ paddingLeft: 12 }}>
                            <label className="text-gray pe-2">
                                <input
                                    name="chkStarred"
                                    type="checkbox"
                                    className="form-check-input"
                                    onChange={props.onIsStarredCheckChanged}
                                    checked={props.isStarred}
                                    disabled={props.readonly}
                                />
                                <i className="fa fa-star-sharp ps-2 pe-1" />
                                Star
                            </label>
                        </div>
                        <div style={{ paddingLeft: 12 }}>
                            <label>
                                <input
                                    name="chkAllowLocalEdit"
                                    type="checkbox"
                                    className="form-check-input"
                                    onChange={props.onLocalEditCheckChanged}
                                    checked={
                                        props.currentNote.allowLocalEdit ||
                                        props.isAllowLocalEditSystemSet
                                    }
                                    disabled={props.readonly || props.currentNote.private}
                                />
                                <span
                                    className={
                                        'float-end ms-2 ' +
                                        (props.isAllowLocalEditSystemSet
                                            ? 'font-green'
                                            : 'text-gray')
                                    }
                                >
                                    <i className="fal fa-map-marker-edit px-1" />
                                    Allow Local Edit
                                    <Help
                                        type={ApplicationHelpType.Info}
                                        title="Allow Local Edit"
                                        helpText="By default, local counsel can create new case notes and view case notes but not edit case notes; “private” notes cannot be viewed by local counsel; “allow local edit” allows local counsel to edit an existing case note."
                                    />
                                </span>
                            </label>
                        </div>
                        <div style={{ paddingLeft: 12 }}>
                            <label className="text-gray">
                                <input
                                    name="chkPrivate"
                                    type="checkbox"
                                    className="form-check-input"
                                    onChange={props.onPrivateCheckChanged}
                                    checked={props.isPrivate}
                                    disabled={props.isPrivateCheckboxDisabled}
                                />
                                <i className="fal fa-location-dot-slash ps-2 pe-1" />
                                Private
                            </label>
                            <Help
                                type={ApplicationHelpType.Info}
                                title="Private"
                                helpText="By default, local counsel can create new case notes and view case notes but not edit case notes; “private” notes cannot be viewed by local counsel; “allow local edit” allows local counsel to edit an existing case note."
                            />
                        </div>
                    </>
                )}

                {props.showHistoryButton && (
                    <IconButton
                        buttonCssClass="btn-no-bg text-gray"
                        icon="fal fa-history"
                        onClick={props.onHistoryButtonClick}
                        text="History"
                    />
                )}

                {props.showWatchListButton && (
                    <IconButton
                        buttonCssClass="btn btn-no-bg text-gray"
                        icon="fal fa-glasses-alt"
                        onClick={props.onWatchListButtonClick}
                        text="Watch List"
                    />
                )}

                {props.showEditButton && (
                    <IconButton
                        buttonCssClass="btn-no-bg text-gray"
                        icon="fal fa-pen"
                        onClick={props.onEditButtonClick}
                        text="Edit"
                    />
                )}

                <Stack direction="horizontal">
                    {!props.readonly && (
                        <>
                            {props.hasUnsavedChanges && (
                                <button
                                    className="btn btn-orange"
                                    onClick={props.onSaveButtonClick}
                                >
                                    Save
                                </button>
                            )}
                            <button className="btn btn-default" onClick={props.onCancelButtonClick}>
                                Cancel
                            </button>
                        </>
                    )}
                    <button className="btn btn-blue" onClick={props.onBackButtonClick}>
                        Back
                    </button>
                </Stack>
            </Stack>
        </Stack>
    );
}

function AddRelatedButton({
    hasItems,
    title,
    icon,
    onClick,
}: {
    hasItems?: boolean;
    title?: string;
    icon?: string;
    onClick: () => void;
}) {
    return (
        <span
            className={`btn btn-no-bg ps-3 ${hasItems ? 'color-orange' : 'text-gray'}`}
            onClick={onClick}
        >
            {icon && <i className={`fa-regular ${icon}`} />}
            <span className="ms-2">{title ?? ''}</span>
        </span>
    );
}

function CaseNoteHeaderTitle(props: { caseTitle?: ICaseTitleModel }) {
    const zoneContext = useZoneContext();

    return (
        <>
            {!isEmpty(props.caseTitle) ? (
                <CaseName caseTitle={props.caseTitle} openLinkInNewTab zoneContext={zoneContext} />
            ) : (
                'Case Note'
            )}
        </>
    );
}
